import { ref, computed, watch, onMounted } from "vue";
import { useDebounceFn } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { useHistoStore } from "@stores/histo";
import { getRegionOptions } from "@utils/constants/regions";
import {
  monthsOptions,
  yearOptions,
  pricerContentTypeOptions,
  pricerContentTopicOptions,
  pricerTargetTypeOptions,
} from "@utils/constants/pricer";
import type { FilterConfig, FilterValues } from "@/components/episto-ui/filter-dropdown";
import { useFiltersUrlSync, arrayParam, stringParam, type UrlFiltersState } from "@/utils/composables/filters/useFiltersUrlSync";

export const HIDDEN_BY_DEFAULT_COLUMNS = ["cpm", "studyType", "ctr", "cpc", "startRate", "cps"];

const urlParams = {
  country: arrayParam(),
  year: arrayParam(),
  month: arrayParam(),
  pricer_content_type: arrayParam(),
  pricer_content_topic: arrayParam(),
  pricer_target_type: arrayParam(),
  account_name: arrayParam(),
  hs_owner_sales: arrayParam(),
  hs_owner_csm: arrayParam(),
  hs_owner_tm: arrayParam(),
  study_type: arrayParam(),
  search: stringParam(),
  target: stringParam(),
  topic: stringParam(),
};

type HistoUrlState = UrlFiltersState<typeof urlParams>;

export const TOGGLEABLE_COLUMNS = [
  { id: "completedAt", label: "Date" },
  { id: "similarity", label: "Similarity" },
  { id: "countryCode", label: "Country" },
  { id: "surveyName", label: "Survey" },
  { id: "studyType", label: "Type" },
  { id: "targetDescription", label: "Target" },
  { id: "completesValidCapped", label: "N" },
  { id: "revenue", label: "Revenue" },
  { id: "recruitmentMargin", label: "Margin" },
  { id: "cpi", label: "CPI" },
  { id: "clientCpi", label: "CPI Client" },
  { id: "completionRate", label: "Comp." },
  { id: "screenoutRate", label: "SO" },
  { id: "loi", label: "LOI" },
  { id: "days", label: "Days" },
  { id: "cpm", label: "CPM" },
  { id: "ctr", label: "CTR" },
  { id: "cpc", label: "CPC" },
  { id: "startRate", label: "Start" },
  { id: "cps", label: "CPS" },
];

interface UseHistoFiltersOptions {
  autoInit?: boolean;
}

export const useHistoFilters = (options: UseHistoFiltersOptions = {}) => {
  const { autoInit = true } = options;

  const histoStore = useHistoStore();
  const { filtersHisto } = storeToRefs(histoStore);
  const { fetchHistos, fetchHistoFilters } = histoStore;

  const filterValues = ref<FilterValues>({});
  const searchQuery = ref("");
  const isLoading = ref(false);

  const aiTargetInput = ref("");
  const aiTopicInput = ref("");
  const aiTarget = ref("");
  const aiTopic = ref("");
  const aiError = ref(false);
  const isAiActive = computed(() => !!aiTarget.value);

  const filterConfigs = computed<FilterConfig[]>(() => [
    {
      id: "country",
      label: "Country",
      hasSearch: true,
      options: getRegionOptions(),
    },
    {
      id: "year",
      label: "Year",
      keepOptionsOrder: true,
      options: yearOptions,
    },
    {
      id: "month",
      label: "Month",
      keepOptionsOrder: true,
      options: monthsOptions,
    },
    {
      id: "pricer_content_type",
      label: "Content type",
      options: pricerContentTypeOptions,
    },
    {
      id: "pricer_content_topic",
      label: "Content topic",
      options: pricerContentTopicOptions,
    },
    {
      id: "pricer_target_type",
      label: "Target type",
      options: pricerTargetTypeOptions,
    },
    {
      id: "account_name",
      label: "Account",
      hasSearch: true,
      options: filtersHisto.value?.account_names || [],
    },
    {
      id: "hs_owner_sales",
      label: "Sales",
      hasSearch: true,
      options: filtersHisto.value?.hs_owner_sales || [],
    },
    {
      id: "hs_owner_csm",
      label: "CSM",
      hasSearch: true,
      options: filtersHisto.value?.hs_owner_csm || [],
    },
    {
      id: "hs_owner_tm",
      label: "TM",
      hasSearch: true,
      options: filtersHisto.value?.hs_owner_tm || [],
    },
    {
      id: "study_type",
      label: "Study type",
      options: [
        { label: "AI Chat", value: "ai_chat" },
        { label: "Quanti", value: "quanti" },
      ],
    },
  ]);

  function buildFiltersPayload(): Record<string, string[]> {
    const filters: Record<string, string[]> = {};
    Object.entries(filterValues.value).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        filters[key] = values as string[];
      }
    });
    return filters;
  }

  function resetAiState() {
    aiTargetInput.value = "";
    aiTopicInput.value = "";
    aiTarget.value = "";
    aiTopic.value = "";
  }

  function serializeUrlState() {
    return { ...buildFiltersPayload(), search: searchQuery.value, target: aiTarget.value, topic: aiTopic.value };
  }

  function applyUrlState(state: HistoUrlState) {
    const { search, target, topic, ...filters } = state;
    filterValues.value = Object.fromEntries(Object.entries(filters).filter(([, values]) => values.length));
    searchQuery.value = search;
    aiTargetInput.value = target;
    aiTopicInput.value = topic;
    aiTarget.value = target;
    aiTopic.value = topic;
  }

  const urlSync = autoInit
    ? useFiltersUrlSync({
        params: urlParams,
        serialize: serializeUrlState,
        apply: applyUrlState,
        onExternalChange: (state) => {
          applyUrlState(state);
          handleSearch();
        },
      })
    : null;

  let requestId = 0;
  let appliedBeforeSearch = { target: "", topic: "" };

  async function runFetch() {
    const id = ++requestId;
    isLoading.value = true;
    urlSync?.syncUrl();
    const ok = await fetchHistos({
      filters: buildFiltersPayload(),
      query: searchQuery.value,
      target: aiTarget.value || null,
      topic: aiTopic.value || null,
    });
    if (id !== requestId) return;
    aiError.value = !!aiTarget.value && !ok;
    isLoading.value = false;
  }

  const handleSearch = useDebounceFn(runFetch, 600);

  async function applyAiSearch() {
    if (!aiTargetInput.value.trim()) return;
    appliedBeforeSearch = { target: aiTarget.value, topic: aiTopic.value };
    aiTarget.value = aiTargetInput.value.trim();
    aiTopic.value = aiTopicInput.value.trim();
    aiError.value = false;
    await runFetch();
  }

  function cancelAiSearch() {
    requestId += 1;
    isLoading.value = false;
    aiTarget.value = appliedBeforeSearch.target;
    aiTopic.value = appliedBeforeSearch.topic;
    aiTargetInput.value = appliedBeforeSearch.target;
    aiTopicInput.value = appliedBeforeSearch.topic;
    urlSync?.syncUrl();
  }

  async function retryAiSearch() {
    aiError.value = false;
    await runFetch();
  }

  async function clearAiSearch() {
    resetAiState();
    aiError.value = false;
    await runFetch();
  }

  function clearFilters() {
    filterValues.value = {};
    searchQuery.value = "";
    resetAiState();
    urlSync?.syncUrl();
  }

  async function init() {
    await fetchHistoFilters();
    isLoading.value = true;
    await fetchHistos({
      filters: buildFiltersPayload(),
      query: searchQuery.value,
      target: aiTarget.value || null,
      topic: aiTopic.value || null,
    });
    isLoading.value = false;
  }

  if (autoInit) {
    urlSync?.restoreFromUrl();
    onMounted(async () => {
      urlSync?.syncUrl();
      await init();
    });
    watch([searchQuery, filterValues], () => handleSearch(), { deep: true });
  }

  return {
    filterConfigs,
    filterValues,
    searchQuery,
    isLoading,
    aiTargetInput,
    aiTopicInput,
    aiTarget,
    aiTopic,
    isAiActive,
    aiError,
    applyAiSearch,
    cancelAiSearch,
    retryAiSearch,
    clearAiSearch,
    clearFilters,
    buildFiltersPayload,
    fetchHistoFilters,
  };
};
